import React, { useState } from 'react'
import { type AssetHolderIdProps } from '../../pages/DashboardHome'
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, TextField } from '@mui/material'
import { useAppDispatch } from '../../../../app/hooks'
import ErrorComponent from '../../../shared/components/ui-widgets/ErrorComponent'
import { Formik } from 'formik'
import {
  type GetActivityStatementUrlForm,
  type GetActivityStatementUrlPayload
} from '../../../../api/schema-dtos/AssetHolderFinancePayloadDtos'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import OtpComponent from '../../../auth/components/OtpComponent'
import { useGetActivityStatementUrlQuery } from '../../../../api/asset-holders/assetHolderApi'
import { setNotification } from '../../../shared/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import { type ActivityStatementSignedUrl } from '../../../../api/schema-dtos/AssetHolderFinanceDomainDtos'
import { format } from 'date-fns'
import { DATE_FORMAT_API } from '../../../../utility/DateFormatter'

const ActivityStatementComponent: React.FC<AssetHolderIdProps> = ({ assetHolderId }: AssetHolderIdProps) => {
  const dispatch = useAppDispatch()
  const [openDialog, toggleDialog] = useState<boolean>(false)

  const defaultForm: GetActivityStatementUrlForm = {
    startDate: new Date(),
    endDate: new Date()
  }

  const [apiGetActivityStatementUrl, { isLoading }] = useGetActivityStatementUrlQuery()

  const handleGetActivityStatementUrl = (formData: GetActivityStatementUrlForm, otp: string): void => {
    const payload: GetActivityStatementUrlPayload = {
      startDate: format(formData.startDate, DATE_FORMAT_API),
      endDate: format(formData.endDate, DATE_FORMAT_API),
      otp
    }

    apiGetActivityStatementUrl({ assetHolderId, ...payload, otp })
      .unwrap()
      .then((response: ActivityStatementSignedUrl) => {
        (window as any).open(response.signedUrl, '_blank')
        toggleDialog(false)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  return (
    <>
      <Button
        variant={'contained'}
        color={'primary'}
        onClick={() => { toggleDialog(true) }}
      >
        Download Activity Statement
      </Button>

      <Dialog
        open={openDialog}
        maxWidth={'sm'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => { toggleDialog(false) }}
        sx={{ p: 2 }}
      >
        <DialogTitle>Download Activity Statement</DialogTitle>

        <DialogContent>
          <Formik
            initialValues={defaultForm}
            onSubmit={(values) => {}}
          >
            {formikProps => (
              <form>
                <Grid container spacing={3} sx={{ mt: 0 }}>
                  {!(formikProps.isValid) && (
                    <Grid item md={12} sx={{ mt: 3 }}>
                      <ErrorComponent title={'Form validation error'} message={Object.values(formikProps.errors).join(', ')}/>
                    </Grid>
                  )}

                  <Grid item md={6} xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Start Date"
                        inputFormat="yyyy/MM/dd"
                        onChange={(newValue: any) => {
                          void formikProps.setFieldValue('startDate', newValue)
                        }}
                        value={formikProps.values.startDate}
                        renderInput={(params: any) => <TextField fullWidth {...params} />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="End Date"
                        inputFormat="yyyy/MM/dd"
                        onChange={(newValue: any) => {
                          void formikProps.setFieldValue('endDate', newValue)
                        }}
                        value={formikProps.values.endDate}
                        renderInput={(params: any) => <TextField fullWidth {...params} />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item md={12}>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <OtpComponent
                        authenticatorConfigured={null}
                        assetHolderId={assetHolderId}
                        actionName={'Download Activity Statement'}
                        isLoading={isLoading}
                        executeAction={(otp: string) => { handleGetActivityStatementUrl(formikProps.values, otp) }}
                        disabled={false}
                      />

                      <Button
                        variant={'outlined'}
                        type="button"
                        sx={{ ml: 2 }}
                        onClick={() => { toggleDialog(false) }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ActivityStatementComponent
